import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./ZoomableImage.module.scss";
import { FaPlus, FaMinus, FaUndo } from "react-icons/fa";

const ZoomableImage = ({ src, alt, className, snapBack = false }) => {
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [touchStartDist, setTouchStartDist] = useState(0);
    const imgRef = useRef(null);
    const containerRef = useRef(null);

    const handleZoomIn = useCallback((e) => {
        e?.stopPropagation();
        setScale((prev) => Math.min(prev + 0.5, 5));
    }, []);

    const handleZoomOut = useCallback((e) => {
        e?.stopPropagation();
        setScale((prev) => {
            const newScale = Math.max(prev - 0.5, 1);
            if (newScale === 1) setPosition({ x: 0, y: 0 });
            return newScale;
        });
    }, []);

    const handleReset = useCallback((e) => {
        e?.stopPropagation();
        setScale(1);
        setPosition({ x: 0, y: 0 });
    }, []);

    // Helper to clamp position so image doesn't fly off screen
    const clampPosition = useCallback((newX, newY, newScale) => {
        if (newScale <= 1) return { x: 0, y: 0 };

        const container = containerRef.current;
        const img = imgRef.current;
        if (!container || !img) return { x: newX, y: newY };

        const containerRect = container.getBoundingClientRect();
        const imgRect = img.getBoundingClientRect();

        // Calculate visible image dimensions after scale
        const scaledWidth = img.clientWidth * newScale;
        const scaledHeight = img.clientHeight * newScale;

        // Boundaries
        const maxX = Math.max(0, (scaledWidth - containerRect.width) / 2);
        const maxY = Math.max(0, (scaledHeight - containerRect.height) / 2);

        return {
            x: Math.min(Math.max(newX, -maxX), maxX),
            y: Math.min(Math.max(newY, -maxY), maxY)
        };
    }, []);

    const handleMouseDown = (e) => {
        if (scale > 1) {
            setIsDragging(true);
            setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && scale > 1) {
            const newX = e.clientX - dragStart.x;
            const newY = e.clientY - dragStart.y;
            setPosition(clampPosition(newX, newY, scale));
        }
    };

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        if (snapBack && scale > 1) {
            handleReset();
        }
    }, [scale, handleReset, snapBack]);

    const handleTouchStart = useCallback((e) => {
        if (e.touches.length === 1 && scale > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.touches[0].clientX - position.x,
                y: e.touches[0].clientY - position.y
            });
            e.stopPropagation();
        } else if (e.touches.length === 2) {
            // e.preventDefault(); // Handled by passive: false listener
            e.stopPropagation();
            const dist = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            setTouchStartDist(dist);
        }
    }, [scale, position]);

    const handleTouchMove = useCallback((e) => {
        if (e.touches.length === 1 && isDragging && scale > 1) {
            const newX = e.touches[0].clientX - dragStart.x;
            const newY = e.touches[0].clientY - dragStart.y;
            setPosition(clampPosition(newX, newY, scale));
            e.stopPropagation();
        } else if (e.touches.length === 2) {
            if (e.cancelable) e.preventDefault();
            e.stopPropagation();

            const touch1 = e.touches[0];
            const touch2 = e.touches[1];

            // Current distance between fingers
            const dist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);

            if (touchStartDist > 0) {
                const deltaScale = dist / touchStartDist;
                const newScale = Math.min(Math.max(scale * deltaScale, 1), 5);

                if (newScale !== scale) {
                    // Zoom towards the midpoint of the two fingers
                    const container = containerRef.current;
                    if (container) {
                        const rect = container.getBoundingClientRect();

                        // Midpoint in screen coordinates
                        const midX = (touch1.clientX + touch2.clientX) / 2;
                        const midY = (touch1.clientY + touch2.clientY) / 2;

                        // Midpoint relative to container center
                        const relX = midX - (rect.left + rect.width / 2);
                        const relY = midY - (rect.top + rect.height / 2);

                        // Calculate how much we need to shift position to keep midpoint stationary
                        // Formula: newPos = mid - (mid - oldPos) * (newScale / oldScale)
                        const scaleRatio = newScale / scale;
                        const newX = relX - (relX - position.x) * scaleRatio;
                        const newY = relY - (relY - position.y) * scaleRatio;

                        setScale(newScale);
                        setPosition(clampPosition(newX, newY, newScale));
                    }
                }
                setTouchStartDist(dist);
            }
        }
    }, [isDragging, scale, position, dragStart, touchStartDist, clampPosition]);

    const handleTouchEnd = useCallback(() => {
        setIsDragging(false);
        setTouchStartDist(0);

        // Reset zoom if snapBack is enabled or on mobile
        if (snapBack || (window.innerWidth <= 768 && scale > 1)) {
            handleReset();
        }
    }, [scale, handleReset, snapBack]);

    const handleWheel = (e) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.2 : 0.2;
            setScale((prev) => {
                const newScale = Math.min(Math.max(prev + delta, 1), 5);
                if (newScale === 1) setPosition({ x: 0, y: 0 });
                return newScale;
            });
        }
    };

    const handleDoubleClick = (e) => {
        e.stopPropagation();
        // If snapback is enabled, we don't want persistent zoom on double click
        if (snapBack) return;

        if (scale > 1) {
            handleReset(e);
        } else {
            setScale(2);
        }
    };

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const onTouchStart = (e) => handleTouchStart(e);
        const onTouchMove = (e) => handleTouchMove(e);
        const onTouchEnd = (e) => handleTouchEnd(e);

        container.addEventListener("wheel", handleWheel, { passive: false });
        container.addEventListener("touchstart", onTouchStart, { passive: false });
        container.addEventListener("touchmove", onTouchMove, { passive: false });
        container.addEventListener("touchend", onTouchEnd, { passive: false });

        return () => {
            container.removeEventListener("wheel", handleWheel);
            container.removeEventListener("touchstart", onTouchStart);
            container.removeEventListener("touchmove", onTouchMove);
            container.removeEventListener("touchend", onTouchEnd);
        };
    }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

    // Check if we are on mobile to hide buttons
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    const hideControls = snapBack || isMobile;

    return (
        <div
            className={styles.zoomContainer}
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp} // This handles snapping back when mouse leaves image
            onDoubleClick={handleDoubleClick}
            style={{
                cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
                touchAction: scale > 1 ? "none" : "auto"
            }}
        >
            <img
                ref={imgRef}
                src={src}
                alt={alt}
                className={`${className} ${styles.zoomImage}`}
                style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    transition: isDragging || touchStartDist > 0 ? "none" : "transform 0.4s cubic-bezier(0.2, 0, 0, 1)",
                    transformOrigin: "center center"
                }}
                draggable={false}
            />

            {!hideControls && (
                <div
                    className={styles.controls}
                    onMouseDown={e => e.stopPropagation()}
                >
                    <button type="button" onClick={handleZoomIn} title="Zoom In"><FaPlus /></button>
                    <button type="button" onClick={handleZoomOut} title="Zoom Out"><FaMinus /></button>
                    <button type="button" onClick={handleReset} title="Reset"><FaUndo /></button>
                </div>
            )}
        </div>
    );
};

export default ZoomableImage;
